import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Building2, Home, Check, ChevronLeft, ChevronRight, CalendarClock, Loader2, Sparkles, Lock } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { EINZEL_LEISTUNGEN_OPTIONS, PAKET_LABEL } from "@/lib/constants";
import { COUNTRIES } from "@/lib/countries";

const emptyForm = {
  objekt_art: "",
  grundstuecksgroesse: "",
  turnus: "einmalig",
  objektgroesse: "",
  anzahl_objekte: "",
  aktueller_dienstleister: "",
  paket_active: false,
  leistungen: [],
  notes: "",
  name: "",
  email: "",
  phone: "",
  address_street: "",
  address_zip: "",
  address_city: "",
  address_country: "Deutschland",
  wunschtermin: "",
};

// Preset form for the Aktionsangebot promo flow – contact info + Wunschtermin only.
const promoForm = {
  ...emptyForm,
  objekt_art: "privat",
  turnus: "einmalig",
  leistungen: [
    "🏷️ Aktionsangebot 99€ (Neukundenpaket)",
    "Unkraut-Entfernung auf Pflasterflächen",
    "Laubentfernung während der Herbstsaison",
    "Rasenmähen vorhandener Grünflächen",
    "Schneiden der Rasenkanten",
  ],
  notes: "Aktionsangebot 99€ · Neukundenpaket (gültig 01.09.–24.12.2026, Umfang nach Absprache)",
};

export default function BookingForm({ open, onOpenChange, promo = false }) {
  const initialStep = promo ? 3 : 1;
  const initialForm = promo ? promoForm : emptyForm;
  const [step, setStep] = useState(initialStep);
  const [form, setForm] = useState(initialForm);
  const [savedLeistungen, setSavedLeistungen] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!open) {
      setStep(initialStep);
      setForm(initialForm);
      setSavedLeistungen([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, promo]);

  useEffect(() => {
    if (step === 4 && slots.length === 0) {
      loadSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  async function loadSlots() {
    setLoadingSlots(true);
    try {
      const today = new Date();
      const inTwoWeeks = new Date(today.getTime() + 14 * 86400_000);
      const fmt = (d) => d.toISOString().slice(0, 10);
      const res = await axios.get(`${API_BASE}/public/available-slots`, {
        params: { from_date: fmt(today), to_date: fmt(inTwoWeeks) },
      });
      setSlots(res.data.slots);
    } catch {
      toast.error("Termine konnten nicht geladen werden.");
    } finally {
      setLoadingSlots(false);
    }
  }

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleLeistung = (l) => {
    if (form.paket_active) return; // locked while Paket active
    setForm((f) => ({
      ...f,
      leistungen: f.leistungen.includes(l)
        ? f.leistungen.filter((x) => x !== l)
        : [...f.leistungen, l],
    }));
  };

  const togglePaket = (active) => {
    if (active) {
      // remember previous manual selection then select all + lock
      setSavedLeistungen(form.leistungen);
      setForm((f) => ({
        ...f,
        paket_active: true,
        leistungen: [...EINZEL_LEISTUNGEN_OPTIONS],
      }));
    } else {
      // restore previous manual selection
      setForm((f) => ({
        ...f,
        paket_active: false,
        leistungen: savedLeistungen,
      }));
    }
  };

  const canProceed = useMemo(() => {
    if (step === 1) return !!form.objekt_art;
    if (step === 2) {
      const hasLeistung = form.paket_active || form.leistungen.length > 0;
      return hasLeistung;
    }
    if (step === 3) {
      return form.name && form.email && form.phone && form.address_street && form.address_zip && form.address_city && form.address_country;
    }
    if (step === 4) return !!form.wunschtermin;
    return false;
  }, [step, form]);

  async function submit() {
    setSubmitting(true);
    try {
      // Build final leistungen list: if package active, include the package label
      // in front so CRM sees it clearly, followed by every included einzel service.
      const finalLeistungen = form.paket_active
        ? [PAKET_LABEL[form.objekt_art], ...EINZEL_LEISTUNGEN_OPTIONS]
        : form.leistungen;
      const payload = {
        ...form,
        leistungen: finalLeistungen,
        grundstuecksgroesse: form.grundstuecksgroesse ? Number(form.grundstuecksgroesse) : null,
        objektgroesse: form.objektgroesse ? Number(form.objektgroesse) : null,
        anzahl_objekte: form.anzahl_objekte ? Number(form.anzahl_objekte) : null,
        aktueller_dienstleister: form.aktueller_dienstleister === "ja" ? true : form.aktueller_dienstleister === "nein" ? false : null,
      };
      delete payload.paket_active;
      await axios.post(`${API_BASE}/public/bookings`, payload);
      toast.success("Anfrage erfolgreich übermittelt!");
      onOpenChange(false);
      nav("/booking-success");
    } catch (e) {
      toast.error("Anfrage fehlgeschlagen. Bitte prüfen Sie Ihre Angaben.");
    } finally {
      setSubmitting(false);
    }
  }

  const totalSteps = promo ? 2 : 4;
  const visibleStep = promo ? step - 2 : step;
  const progress = ((visibleStep - 1) / (totalSteps - 1)) * 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white p-0 gap-0 border-[#E5E7EB]">
        <DialogHeader className="p-6 pb-3 border-b border-[#E5E7EB]">
          <DialogTitle className="font-display text-2xl text-[#1A201A]">
            {promo
              ? "Aktionsangebot sichern – in 2 Schritten"
              : "Termin sichern – in 4 Schritten"}
          </DialogTitle>
          {promo && (
            <p className="text-xs text-[#5C6B5D] mt-1">
              Neukundenpaket – wir übernehmen: Rasenmähen · Rasenkanten · Unkraut- &amp; Laubentfernung.
            </p>
          )}
          <div className="mt-4">
            <Progress value={progress} className="h-1.5" />
            <div className="flex justify-between mt-2 text-xs text-[#5C6B5D]">
              <span>Schritt {visibleStep} von {totalSteps}</span>
              <span>
                {step === 1 && "Objektart"}
                {step === 2 && "Details"}
                {step === 3 && "Kontakt"}
                {step === 4 && "Wunschtermin"}
              </span>
            </div>
          </div>
        </DialogHeader>

        <div className="p-6 max-h-[65vh] overflow-y-auto">
          {step === 1 && (
            <div className="space-y-5">
              <p className="text-sm text-[#5C6B5D]">Was möchten Sie pflegen lassen?</p>
              <RadioGroup
                value={form.objekt_art}
                onValueChange={(v) => update({ objekt_art: v })}
                className="grid sm:grid-cols-2 gap-4"
              >
                {[
                  { v: "privat", label: "Ein-/Mehrfamilienhaus", desc: "Privates Grundstück", Icon: Home },
                  { v: "gewerbe", label: "Gewerbe / Firmenobjekt", desc: "Firmengelände, Verwaltung, Anlage", Icon: Building2 },
                ].map((o) => (
                  <label
                    key={o.v}
                    htmlFor={`objekt-${o.v}`}
                    data-testid={`booking-objekt-${o.v}`}
                    className={`cursor-pointer border rounded-xl p-5 transition-all ${
                      form.objekt_art === o.v
                        ? "border-[#6B7F3E] bg-[#F5F6F0] ring-2 ring-[#6B7F3E]/20"
                        : "border-[#E5E7EB] hover:border-[#6B7F3E]/50"
                    }`}
                  >
                    <RadioGroupItem id={`objekt-${o.v}`} value={o.v} className="sr-only" />
                    <o.Icon className="w-8 h-8 text-[#6B7F3E] mb-3" strokeWidth={1.5} />
                    <div className="font-medium text-[#1A201A]">{o.label}</div>
                    <div className="text-xs text-[#5C6B5D] mt-1">{o.desc}</div>
                  </label>
                ))}
              </RadioGroup>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              {form.objekt_art === "privat" ? (
                <>
                  <div>
                    <Label>Turnus *</Label>
                    <RadioGroup
                      value={form.turnus}
                      onValueChange={(v) => update({ turnus: v })}
                      className="grid grid-cols-2 gap-3 mt-1.5"
                    >
                      {[
                        { v: "einmalig", l: "Einmalig" },
                        { v: "regelmaessig", l: "Regelmäßig" },
                      ].map((o) => (
                        <label
                          key={o.v}
                          htmlFor={`turnus-${o.v}`}
                          data-testid={`booking-turnus-${o.v}`}
                          className={`cursor-pointer border rounded-lg p-3 text-center text-sm ${
                            form.turnus === o.v ? "border-[#6B7F3E] bg-[#F5F6F0]" : "border-[#E5E7EB]"
                          }`}
                        >
                          <RadioGroupItem id={`turnus-${o.v}`} value={o.v} className="sr-only" />
                          {o.l}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label htmlFor="anzahl_objekte">Anzahl Objekte</Label>
                    <Input
                      id="anzahl_objekte"
                      data-testid="booking-anzahl-objekte"
                      type="number"
                      className="mt-1.5"
                      value={form.anzahl_objekte}
                      onChange={(e) => update({ anzahl_objekte: e.target.value })}
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <Label>Aktueller Dienstleister vorhanden?</Label>
                    <RadioGroup
                      value={form.aktueller_dienstleister}
                      onValueChange={(v) => update({ aktueller_dienstleister: v })}
                      className="grid grid-cols-2 gap-3 mt-1.5"
                    >
                      {[
                        { v: "ja", l: "Ja" },
                        { v: "nein", l: "Nein" },
                      ].map((o) => (
                        <label
                          key={o.v}
                          htmlFor={`dl-${o.v}`}
                          data-testid={`booking-dienstleister-${o.v}`}
                          className={`cursor-pointer border rounded-lg p-3 text-center text-sm ${
                            form.aktueller_dienstleister === o.v ? "border-[#6B7F3E] bg-[#F5F6F0]" : "border-[#E5E7EB]"
                          }`}
                        >
                          <RadioGroupItem id={`dl-${o.v}`} value={o.v} className="sr-only" />
                          {o.l}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                </>
              )}
              <div>
                <Label>Gewünschte Leistungen *</Label>
                <p className="text-xs text-[#5C6B5D] mt-1">
                  Entweder das komplette Rundum-Sorglos-Paket wählen oder einzelne Leistungen kombinieren.
                </p>

                {/* Rundum-Sorglos-Paket – context-aware based on step 1 selection */}
                <button
                  type="button"
                  onClick={() => togglePaket(!form.paket_active)}
                  data-testid="booking-paket-toggle"
                  className={`mt-3 w-full text-left rounded-xl border-2 p-4 transition-all ${
                    form.paket_active
                      ? "border-[#6B7F3E] bg-[#2C3E2D] text-white"
                      : "border-[#C4CFB1] bg-[#F5F6F0] hover:border-[#6B7F3E]"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                      form.paket_active ? "bg-[#6B7F3E] text-white" : "bg-white text-[#6B7F3E]"
                    }`}>
                      <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`text-[10px] font-medium uppercase tracking-wider mb-0.5 ${
                        form.paket_active ? "text-[#C4CFB1]" : "text-[#6B7F3E]"
                      }`}>
                        Empfohlen · Alle Leistungen inklusive
                      </div>
                      <div className={`font-display text-base font-semibold ${
                        form.paket_active ? "text-white" : "text-[#1A201A]"
                      }`}>
                        {PAKET_LABEL[form.objekt_art]}
                      </div>
                      <div className={`text-xs mt-1 ${form.paket_active ? "text-[#B8C1A9]" : "text-[#5C6B5D]"}`}>
                        Ganzjährige Rundum-Betreuung mit allen unten aufgeführten Leistungen.
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      form.paket_active ? "border-white bg-white" : "border-[#C4CFB1] bg-white"
                    }`}>
                      {form.paket_active && <Check className="w-4 h-4 text-[#6B7F3E]" strokeWidth={3} />}
                    </div>
                  </div>
                </button>

                {/* Separator */}
                <div className="flex items-center gap-2 my-4">
                  <div className="h-px bg-[#E5E7EB] flex-1" />
                  <span className="text-[10px] font-medium uppercase tracking-wider text-[#5C6B5D]">
                    oder einzelne Leistungen
                  </span>
                  <div className="h-px bg-[#E5E7EB] flex-1" />
                </div>

                {/* Einzelleistungen – disabled while Paket active */}
                <div
                  data-testid="booking-einzel-container"
                  className={`grid grid-cols-2 gap-2 max-h-56 overflow-y-auto p-1 transition-opacity ${
                    form.paket_active ? "opacity-60 pointer-events-none" : ""
                  }`}
                  aria-disabled={form.paket_active}
                >
                  {EINZEL_LEISTUNGEN_OPTIONS.map((l) => {
                    const checked = form.paket_active || form.leistungen.includes(l);
                    return (
                      <label
                        key={l}
                        className={`flex items-center gap-2 p-2.5 border rounded-lg text-sm select-none ${
                          form.paket_active ? "cursor-not-allowed" : "cursor-pointer"
                        } ${
                          checked
                            ? "border-[#6B7F3E] bg-[#F5F6F0]"
                            : "border-[#E5E7EB] hover:border-[#6B7F3E]/50"
                        }`}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={form.paket_active}
                          onCheckedChange={() => toggleLeistung(l)}
                          data-testid={`booking-leistung-${l.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        />
                        <span className={form.paket_active ? "text-[#4A5D4E]" : ""}>{l}</span>
                        {form.paket_active && (
                          <Lock className="w-3 h-3 ml-auto text-[#95A97A]" strokeWidth={2} />
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label htmlFor="booking-notes">Weitere Hinweise</Label>
                <Textarea
                  id="booking-notes"
                  data-testid="booking-notes"
                  className="mt-1.5"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => update({ notes: e.target.value })}
                  placeholder="Besonderheiten, Zugang, Wünsche…"
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label htmlFor="name">Vor- und Nachname *</Label>
                <Input
                  id="name" data-testid="booking-name" className="mt-1.5"
                  value={form.name} onChange={(e) => update({ name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="email">E-Mail *</Label>
                <Input
                  id="email" type="email" data-testid="booking-email" className="mt-1.5"
                  value={form.email} onChange={(e) => update({ email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefon *</Label>
                <Input
                  id="phone" data-testid="booking-phone" className="mt-1.5"
                  value={form.phone} onChange={(e) => update({ phone: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="street">Straße & Hausnr. *</Label>
                <Input
                  id="street" data-testid="booking-street" className="mt-1.5"
                  value={form.address_street} onChange={(e) => update({ address_street: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="zip">PLZ *</Label>
                <Input
                  id="zip" data-testid="booking-zip" className="mt-1.5"
                  value={form.address_zip} onChange={(e) => update({ address_zip: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="city">Ort *</Label>
                <Input
                  id="city" data-testid="booking-city" className="mt-1.5"
                  value={form.address_city} onChange={(e) => update({ address_city: e.target.value })}
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="country">Land *</Label>
                <Select
                  value={form.address_country}
                  onValueChange={(v) => update({ address_country: v })}
                >
                  <SelectTrigger
                    id="country"
                    data-testid="booking-country"
                    className="mt-1.5 h-10"
                  >
                    <SelectValue placeholder="Land wählen" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {COUNTRIES.map((c) => (
                      <SelectItem
                        key={c.code}
                        value={c.name}
                        data-testid={`booking-country-option-${c.code}`}
                      >
                        <span className="mr-2">{c.flag}</span>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <div className="flex items-center gap-2 text-sm text-[#5C6B5D] mb-4">
                <CalendarClock className="w-4 h-4 text-[#6B7F3E]" />
                Freie Termine der nächsten 2 Wochen (Mo–Fr, 08:00–17:00)
              </div>
              {loadingSlots ? (
                <div className="flex items-center justify-center py-10 text-[#5C6B5D]">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Termine werden geladen…
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 max-h-[42vh] overflow-y-auto pr-1">
                  {(() => {
                    const grouped = slots.reduce((acc, s) => {
                      acc[s.date] = acc[s.date] || [];
                      acc[s.date].push(s);
                      return acc;
                    }, {});
                    return Object.entries(grouped).slice(0, 10).map(([date, list]) => (
                      <div key={date}>
                        <div className="text-xs font-medium text-[#2C3E2D] mb-1.5">
                          {list[0].label.split(" ").slice(0, 2).join(" ")}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {list.map((s) => (
                            <button
                              key={s.iso}
                              type="button"
                              data-testid={`booking-slot-${s.iso}`}
                              onClick={() => update({ wunschtermin: s.iso })}
                              className={`text-sm px-3 py-1.5 rounded-full border transition-all ${
                                form.wunschtermin === s.iso
                                  ? "bg-[#6B7F3E] text-white border-[#6B7F3E]"
                                  : "border-[#E5E7EB] text-[#2C3E2D] hover:border-[#6B7F3E] hover:bg-[#F5F6F0]"
                              }`}
                            >
                              {s.time}
                            </button>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                  {slots.length === 0 && (
                    <div className="text-sm text-[#5C6B5D] p-6 text-center">
                      Aktuell keine Slots verfügbar. Wir melden uns nach Ihrer Anfrage.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-[#E5E7EB] bg-[#FAFAF5] rounded-b-lg">
          <Button
            variant="ghost"
            onClick={() => setStep((s) => Math.max(initialStep, s - 1))}
            disabled={step === initialStep}
            data-testid="booking-back-btn"
            className="text-[#2C3E2D]"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          {step < 4 ? (
            <Button
              onClick={() => setStep((s) => s + 1)}
              disabled={!canProceed}
              data-testid="booking-next-btn"
              className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white rounded-full px-6"
            >
              Weiter <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={submit}
              disabled={!canProceed || submitting}
              data-testid="booking-submit-btn"
              className="bg-[#6B7F3E] hover:bg-[#5A6B34] text-white rounded-full px-6"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Anfrage absenden
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
